<template>
    <tr v-if="index !== 0" class="separator">
        <td class="separator-td" />
        <th scope="col" class="separator-td" v-for="item in rowData.values" :key="item.id" />
    </tr>
    <tr :class="rowClass">
        <th scope="row">
            <span class="title" @click="tdClick(rowData.id, rowData.noeudsEnfants)" @keydown.enter.prevent="tdClick(rowData.id, rowData.noeudsEnfants)" @keydown.space.prevent="tdClick(rowData.id, rowData.noeudsEnfants)" tabindex="0" role="button">
                <template v-if="rowData.noeudsEnfants.length > 0 && open">
                    <img class="upAllRow" :src="this.chevronUp" :alt="$t('alt.replier')" />
                </template>
                <template v-else-if="rowData.noeudsEnfants.length > 0 && !open">
                    <img class="downAllRow" :src="this.chevronDown" :alt="$t('alt.deplier')" />
                </template>
                {{ rowData.libelle }}
            </span>
        </th>
        <td
            v-for="item in rowData.periodesPlanning"
            :key="item.id"
            :class="{
                selectable: selectionMode && item.etat === 'REALISE' && isLeaf,
                selected: selectionMode && item.etat === 'REALISE' && isLeaf && isSelected(item)
            }"
            @click="selectionMode && item.etat === 'REALISE' && isLeaf ? onCellClick(item) : null"
        >
            <div v-if="displayPeriodiciteOrNot" :tabindex="selectionMode && item.etat === 'REALISE' && isLeaf ? 0 : -1" :role="selectionMode && item.etat === 'REALISE' && isLeaf ? 'button' : null" @keydown.enter.prevent="selectionMode && item.etat === 'REALISE' && isLeaf ? onCellClick(item) : null" @keydown.space.prevent="selectionMode && item.etat === 'REALISE' && isLeaf ? onCellClick(item) : null">
                <span>
                    {{ getPeriodiciteString(item.periodicite) }}
                </span>
                <span>
                    <img v-if="item.etat" class="statusIcon" :src="getStatusIcon(item.etat)" :alt="getStatusName(item.etat)" />
                </span>
                <div>
                    <img v-if="item.isOberservationAlarmante" :src="this.warning" :alt="$t('alt.observation.alarmante')" />
                </div>
            </div>
        </td>
    </tr>
    <TableRow
        v-for="item in displayedChildren"
        :rowData="item"
        :key="item.libelle"
        :rowClass="getChildClass"
        :openAll="openAll"
        :selectionMode="selectionMode"
        :selected-report-ids="selectedReportIds"
        :selected-ui-ids="selectedUiIds"
        @open="emitOpen"
        @emitOpenModal="emitOpenModal"
        @cellClick="$emit('cellClick', $event)"
    />
</template>

<script lang="ts">
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { RowClass, TableRowData } from '@/modules/Preventif/components/TreeGrid.vue';
import { defineComponent, PropType } from 'vue';
import StringUtils from '@/common/utils/StringUtils';

export default defineComponent({
    data() {
        return {
            open: this.openAll,
        };
    },
    props: {
        rowData: { type: Object as PropType<TableRowData>, required: true },
        rowClass: { type: String as PropType<RowClass>, required: false },
        openAll: { type: Boolean, required: false },
        index: { type: Number, required: false },
        selectionMode: { type: Boolean, required: false, default: false },
        selectedReportIds: { type: Array as PropType<string[]>, required: false, default: () => [] },
        selectedUiIds: { type: Array as PropType<string[]>, required: false, default: () => [] },
    },
    computed: {
        checkboxMarkedCircle() {
            return require('../../../assets/img/checkbox-marked-circle.svg');
        },
        checkboxMarkedCircleSemi() {
            return require('@/assets/img/checkbox-marked-circle-semi.svg');
        },
        checkboxMarkedCircleVoid() {
            return require('@/assets/img/checkbox-marked-circle-void.svg');
        },
        chevronUp() {
            return require('@/assets/img/chevron-up.svg');
        },
        chevronDown() {
            return require('@/assets/img/chevron-down.svg');
        },
        warning() {
            return require('@/assets/img/warning-yellow.svg');
        },
        displayedChildren(): TableRowData[] {
            return this.open ? this.rowData.noeudsEnfants : [];
        },
        // Un noeud parent possède des enfants; on interdit la sélection sur ces lignes
        isLeaf(): boolean {
            try {
                return !(this as any).rowData?.noeudsEnfants || (this as any).rowData?.noeudsEnfants.length === 0;
            } catch (e) {
                return true;
            }
        },
        getChildClass() {
            switch (this.rowClass) {
                case RowClass.first:
                    return RowClass.second;
                case RowClass.second:
                    return RowClass.third;
                default:
                    return RowClass.fourth;
            }
        },
        displayPeriodiciteOrNot(): boolean{
            return this.rowClass != RowClass.second;
        }
    },
    emits: ['open', 'emitOpenModal', 'cellClick'],
    methods: {
        tdClick(id: string, children: any[]) {
            if (children.length > 0) {
                this.toggleOpen();
            } else {
                this.emitOpenModal(id);
            }
        },
        emitOpenModal(id: string) {
            this.$emit('emitOpenModal', id);
        },
        onCellClick(period: any) {
            // remonter un événement avec les infos utiles
            const reportId = (period && (period.uniqueDocumentId || period.id)) || undefined;
            const uiId = `${(this as any).rowData.id}|${period?.dateDebut || ''}|${period?.dateFin || ''}`;
            const interventionId = (this as any).rowData?.interventionId;
            this.$emit('cellClick', { rowId: (this as any).rowData.id, period, reportId, uiId, interventionId });
        },
        isSelected(period: any): boolean {
            const reportId = (period && (period.uniqueDocumentId || period.id)) || undefined;
            const uiId = `${(this as any).rowData.id}|${period?.dateDebut || ''}|${period?.dateFin || ''}`;
            const byReportId = reportId ? (this as any).selectedReportIds?.includes(reportId) : false;
            const byUiId = (this as any).selectedUiIds?.includes(uiId);
            return !!(byReportId || byUiId);
        },
        toggleOpen() {
            this.emitOpen;
            this.open = !this.open;
        },
        emitOpen() {
            this.$emit('open');
        },
        getStatusIcon(state: string) {
            return state === 'REALISE'
                ? this.checkboxMarkedCircle
                : state === 'EN_COURS'
                ? this.checkboxMarkedCircleSemi
                : state === 'A_REALISER'
                ? this.checkboxMarkedCircleVoid
                : '';
        },
        getStatusName(state: string) {
            return statusName(state);
        },
        getPeriodiciteString(periodicite : number) : string {
            return periodicite != undefined ? StringUtils.getPeriodiciteAsString(periodicite) : '';
        },
    },
    watch: {
        openAll(value) {
            if (value) {
                this.open = true;
            } else if (value === false) {
                this.open = false;
            }
        },
    },
});
</script>

<style scoped>
table {
    background: white;
    border-collapse: collapse;
    width: 100%;
}
table thead tr:first-child {
    border-bottom: 1px solid #c4c4c4;
}
table thead tr:first-child th:first-child {
    height: 115px;
    width: 350px;
}
table thead tr:first-child th {
    text-align: center;
    vertical-align: bottom;
    font-family: 'Raleway Semi Bold', sans-serif;
    padding-bottom: 16px;
    border-top: none;
}
table td:first-child, table th:first-child {
    border-left: none;
}
table td:last-child, table th:last-child {
    border-right: none;
}
table td, table th {
    border-left: 1px solid #c4c4c4;
    border-right: 1px solid #c4c4c4;
    padding: 8px 4px 8px 4px;
    height: 30px;
}
.selectable {
    cursor: pointer;
}
.selectable:hover {
    background-color: #f5f8ff;
}
.selected {
    background-color: #e8f0ff; /* Couleur plus marquée pour la sélection */
    outline: 1px solid #9ab9ff;
}
table td > div {
    display: flex;
    flex-direction: column;
    align-items: center;
}
table td > div > span {
    margin-bottom: 8px;
    width: 100%;
}
table td > div > div {
    display: flex;
    justify-content: center;
}
table tr {
    margin-top: 4px;
}
table tbody td:not(:first-child) span {
    display: flex;
    align-items: center;
    justify-content: center;
}
table tbody td:first-child span,
table tbody th:first-child span {
    display: flex;
    align-items: center;
}
table tbody td:first-child span .upAllRow,
table tbody td:first-child span .downAllRow,
table tbody th:first-child span .upAllRow,
table tbody th:first-child span .downAllRow {
    margin-right: 8px;
    width: 38px;
    height: 38px;
}
table tbody td:first-child span,
table tbody th:first-child span {
    cursor: pointer;
}
.first {
    background: rgba(0, 0, 0, 0.2);
}
.second {
    background: rgba(0, 0, 0, 0.1);
}
.third {
    background: rgba(0, 0, 0, 0.05);
}
.second > td:first-child span, .second > th:first-child span {
    padding-left: 20px;
}
.third > td:first-child span, .third > th:first-child span {
    padding-left: 40px;
}
.fourth > td:first-child span, .fourth > th:first-child span {
    padding-left: 60px;
}
.upAll,
.downAll {
    cursor: pointer;
}
.downAll {
    margin-left: 20px;
}
.title {
    font-family: 'Raleway Semi Bold', sans-serif;
    justify-content: flex-start;
    text-align: left;
}
.separator {
    height: 3px;
    background-color: var(--white);
}
.separator-td {
    height: 3px;
    padding: 0;
}
.statusIcon{
    height: 24px;
    width: 24px;
}
</style>
